/**
 * Tableau de bord.
 *
 * La premiere chose que voit un utilisateur. Le CDC lui assigne trois
 * questions, et rien d'autre :
 *   1. Ou en sont mes envois ?
 *   2. Puis-je encore signer ?
 *   3. Qu'est-ce qui demande mon attention ?
 *
 * La troisieme est la plus utile et la plus facile a rater : un ecran qui
 * se contente d'aligner des compteurs oblige l'utilisateur a chercher
 * lui-meme ce qui ne va pas.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, type EnveloppeResume, type Solde } from "../api";
import { Chargement, Erreur, Statut, Vide, date, montant, useRelectureAuRetour } from "../components/ui";

interface Props {
  onOuvrirEnveloppe: (id: string) => void;
  onNouvelleEnveloppe: () => void;
  onOuvrirFacturation: () => void;
  /** Traitement avant signature (12/10/2026) : ouvrir « À traiter ». */
  onOuvrirATraiter?: () => void;
}

/** En dessous, on previent : une enveloppe refusee faute de credit au
 *  mauvais moment coute plus qu'une recharge anticipee. */
const SEUIL_CREDIT_BAS_MINOR = 2000;

export function Dashboard({ onOuvrirEnveloppe, onNouvelleEnveloppe, onOuvrirFacturation, onOuvrirATraiter }: Props) {
  // Rappel « À traiter » : un confort, son echec n'empeche rien.
  const [aTraiter, setATraiter] = useState(0);
  useEffect(() => {
    void api.aTraiter().then((r) => setATraiter(r.a_traiter.length), () => undefined);
  }, []);
  const [solde, setSolde] = useState<Solde | null>(null);
  const [enveloppes, setEnveloppes] = useState<EnveloppeResume[]>([]);
  const [total, setTotal] = useState(0);
  const [erreur, setErreur] = useState<unknown>(null);
  const [chargement, setChargement] = useState(true);

  const monte = useRef(true);
  const charger = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([api.solde(), api.enveloppes(10)]);
      if (!monte.current) return;
      setSolde(s);
      setEnveloppes(e.envelopes ?? []);
      setTotal(e.total ?? 0);
      setErreur(null);
    } catch (e2) {
      if (monte.current) setErreur(e2);
    } finally {
      if (monte.current) setChargement(false);
    }
  }, []);

  useEffect(() => {
    monte.current = true;
    void charger();
    return () => {
      monte.current = false;
    };
  }, [charger]);

  // Solde et envois relus au retour sur l'onglet (recharge faite ailleurs,
  // enveloppe signee entre-temps), sans ecran de chargement.
  useRelectureAuRetour(() => void charger());

  if (chargement) return <Chargement />;

  const enCours = enveloppes.filter((e) => ["sent", "in_progress", "processing"].includes(e.status));
  const enEchec = enveloppes.filter((e) => e.outcome === "failure" || ["failed", "declined"].includes(e.status));
  const creditBas = solde !== null && solde.balance_minor < SEUIL_CREDIT_BAS_MINOR;

  return (
    <>
      <h1 className="page-title">Tableau de bord</h1>
      <p className="page-lead">Vue d'ensemble de vos envois et de votre crédit.</p>

      <Erreur erreur={erreur} />

      {/* 3. Ce qui demande une action, EN PREMIER : le reste est du
          contexte, ceci appelle un geste. */}
      {(enEchec.length > 0 || creditBas) && (
        <div className="stack" style={{ marginBottom: "1.25rem" }}>
          {enEchec.length > 0 && (
            <div className="alert alert--error" role="alert">
              <strong>
                {enEchec.length === 1
                  ? "Une enveloppe demande votre attention"
                  : `${enEchec.length} enveloppes demandent votre attention`}
              </strong>
              <p style={{ margin: "0.4rem 0 0" }}>
                {enEchec.map((e) => e.title).join(", ")}
              </p>
            </div>
          )}
          {creditBas && (
            <div className="alert alert--error" role="alert">
              <strong>Crédit bas : {montant(solde?.balance_minor, solde?.currency_code)}.</strong>
              <p style={{ margin: "0.4rem 0 0.6rem" }}>
                Un virement met quelques jours à être encaissé : anticipez pour ne pas être
                bloqué au moment d'envoyer.
              </p>
              <button type="button" className="btn btn--primary" onClick={onOuvrirFacturation}>
                Recharger mon compte
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid" style={{ marginBottom: "1.5rem" }}>
        <div className="stat">
          <p className="stat__label">Crédit disponible</p>
          <p className="stat__value">{montant(solde?.balance_minor, solde?.currency_code)}</p>
        </div>
        <div className="stat">
          <p className="stat__label">Envois en cours</p>
          <p className="stat__value">{enCours.length}</p>
        </div>
        <div className="stat">
          <p className="stat__label">Enveloppes au total</p>
          <p className="stat__value">{total}</p>
        </div>
      </div>

      {aTraiter > 0 && onOuvrirATraiter && (
        // Ce qui attend l'utilisateur avant une signature (12/10/2026).
        <div className="alert alert--info" role="status" style={{ marginBottom: "1rem" }}>
          <strong>{aTraiter} document{aTraiter > 1 ? "s" : ""} vous attend{aTraiter > 1 ? "ent" : ""} avant signature.</strong>{" "}
          <button type="button" className="btn btn--link btn--inline" onClick={onOuvrirATraiter}>
            Ouvrir « À traiter »
          </button>
        </div>
      )}

      <div className="card">
        {/* Porteur, 12/10/2026 : « Nouvelle enveloppe » n'a plus sa place ici
            (l'envoi part de « Signer »). */}
        <h2 className="card__title" style={{ margin: "0 0 1rem", fontSize: "1.1rem" }}>
          Dernières enveloppes
        </h2>

        {enveloppes.length === 0 ? (
          <Vide>
            <p>Aucune enveloppe pour le moment.</p>
            <button type="button" className="btn btn--primary" onClick={onNouvelleEnveloppe}>
              Créer ma première enveloppe
            </button>
          </Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>État</th>
                  <th>Créée le</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {enveloppes.map((e) => (
                  <tr key={e.id}>
                    <td>{e.title}</td>
                    <td>
                      <Statut valeur={e.status} />
                    </td>
                    <td>{date(e.created_at)}</td>
                    <td className="num">
                      <button
                        type="button"
                        className="btn btn--link"
                        onClick={() => onOuvrirEnveloppe(e.id)}
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
